import React, { useState } from 'react';
import { Button, Flex, Input, Typography } from 'antd';
import { EditOutlined, FormOutlined } from '@ant-design/icons';
import { CardNoteSide, MAX_CARD_NOTE_LENGTH } from '@lingoping/core';

const { Text } = Typography;

interface Props {
  side: CardNoteSide;
  /** The saved note, if any. */
  value?: string | null;
  /** Saves the text; an empty text removes the note. */
  onSave: (text: string) => Promise<void> | void;
}

/**
 * The learner's own note on one side of a card: "Add a note" while there is none, the note with
 * an edit button once there is one. Clicks and keys stay inside it, so writing a note never flips
 * or rates the card it sits on.
 */
export const CardNote: React.FC<Props> = ({ side, value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const note = value?.trim() ? value : null;

  const start = () => {
    setDraft(note ?? '');
    setEditing(true);
  };
  const save = async (text: string) => {
    setSaving(true);
    try {
      await onSave(text);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{ width: '100%' }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {editing ? (
        <div>
          <Input.TextArea
            autoFocus
            dir="auto"
            value={draft}
            maxLength={MAX_CARD_NOTE_LENGTH}
            showCount
            autoSize={{ minRows: 2, maxRows: 6 }}
            aria-label={`Your note on the ${side} of this card`}
            placeholder="A memory aid, a rule, an example of your own…"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditing(false);
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void save(draft);
            }}
          />
          <Flex gap={8} justify="flex-end" style={{ marginTop: 22 }}>
            {note && (
              <Button
                size="small"
                danger
                type="text"
                disabled={saving}
                onClick={() => void save('')}
              >
                Delete note
              </Button>
            )}
            <Button size="small" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="small" type="primary" loading={saving} onClick={() => void save(draft)}>
              Save note
            </Button>
          </Flex>
        </div>
      ) : note ? (
        <Flex
          align="start"
          gap={8}
          style={{
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 8,
            padding: '8px 10px',
            textAlign: 'start',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontSize: 11, fontWeight: 600, color: '#92400e', display: 'block' }}>
              Your note
            </Text>
            <Text
              dir="auto"
              style={{
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                display: 'block',
              }}
            >
              {note}
            </Text>
          </div>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            aria-label={`Edit your note on the ${side}`}
            onClick={start}
          />
        </Flex>
      ) : (
        <Button
          type="text"
          size="small"
          icon={<FormOutlined />}
          onClick={start}
          style={{ color: '#475569', fontSize: 12 }}
        >
          Add a note
        </Button>
      )}
    </div>
  );
};
